/** `2026-10-16` or an ISO timestamp → `16/10/2026`, in Lisbon time. */
export function formatDate(value: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value);
  return date.toLocaleDateString('pt-PT', { timeZone: 'Europe/Lisbon' });
}

/** ISO timestamp → `16/10/2026, 21:30`, in Lisbon time. */
export function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('pt-PT', {
    timeZone: 'Europe/Lisbon',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/** `95` → `1 h 35 min`; `40` → `40 min`. */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  if (h === 0) return `${String(m)} min`;
  return m % 60 === 0 ? `${String(h)} h` : `${String(h)} h ${String(m % 60)} min`;
}

/** `1234` → `1234`, with the Portuguese thousands separator from 10 000 up. */
export function formatCount(value: number): string {
  return value.toLocaleString('pt-PT');
}

/** `+12`, `-3`, `0`. */
export function formatChange(value: number): string {
  return value > 0 ? `+${formatCount(value)}` : formatCount(value);
}

const LISBON_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Lisbon',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function lisbonParts(date: Date): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of LISBON_PARTS.formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return out;
}

/** `2026-11-14T21:00` (hora de Lisboa, do `DateTimeInput`) → ISO. `null` se vazio ou inválido. */
export function lisbonInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  const wanted = Date.UTC(y, mo - 1, d, h, mi);
  // O desvio de Lisboa nesse instante (0 ou +1 h); duas voltas acertam na mudança de hora.
  let guess = wanted;
  for (let i = 0; i < 2; i++) {
    const p = lisbonParts(new Date(guess));
    guess += wanted - Date.UTC(p.year ?? 0, (p.month ?? 1) - 1, p.day, p.hour, p.minute);
  }
  return new Date(guess).toISOString();
}

/** ISO → `21:30`, em hora de Lisboa. */
export function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString('pt-PT', {
    timeZone: 'Europe/Lisbon',
    hour: '2-digit',
    minute: '2-digit',
  });
}
